<template>
  <form class="needs-validation ml-0" @submit.prevent="update" novalidate>
    <div class="col px-0 pr-lg-3" v-if="horse ? horse.id ? true : false : false">
      <section class="bg-white rounded mb-4 p-3 p-sm-4">
        <div class="d-flex justify-content-between align-items-start">
          <h1 class="h2 mb-3">Horse Profile</h1>
          <span class="badge badge-info" v-if="horse.registration_date">Registered</span>
          <span class="badge alert-warning" v-else>Unregistered</span>
        </div>
        <div role="alert" class="alert alert-warning" v-if="errors">{{errors}}</div>
        <div role="alert" class="alert alert-warning" v-if="!horse.active">Please activate the horse to save horse data.</div>
        <div class="row">
          <div class="col-12 form-group" v-if="(user === 'Admin' || user === 'SuperAdmin')">
            <label for="notes">Notes</label>
            <textarea class="form-control" id="notes" v-model="horse.notes" :disabled="!is_owner && !userIsSecretary"></textarea>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label role="presentation">&nbsp;</label>
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input" id="active" v-model="horse.active">
              <label class="custom-control-label" for="active">Active</label>
            </div>
          </div>

          <div class="col-12 col-md-6 form-group mb-md-1">
            <label for="horseMember">Rider/s</label>
            <div :class="['align-items-start', {'click-events-none not-required': !horse.active || (!is_owner && !userIsSecretary)}]">
              <member-search-select
                element-id="horseMember"
                context="owner"
                multiple
                :limit="10"
                :value="selectedMembers"
                :initial-options="memberInitialOptions"
                @input="v => { selectedMembers = v }"
              />
              <div class="invalid-tooltip" :style="isUserInvalid ? 'display: block': ''">Please include the Rider/s associated with the horse.</div>
            </div>
            <small>Add here all of the members that need to nominate on this horse.</small>
            <small class="text-danger" v-if="horse.user && horse.user.deleted_at && horse.user.id === Number(horse.user_id[0])">Deleted</small>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6 form-group">
            <label for="horseName">Horse Name</label>
            <input type="text" class="form-control" id="horseName" required v-model="horse.name" :disabled="!horse.active || (!is_owner && !userIsSecretary)">
            <div class="invalid-tooltip">Please include the Horse Name.</div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label for="horseOwner">Owner</label>
            <div :class="['align-items-start', { 'click-events-none not-required' : (!horse.active || !(user === 'Admin' || user === 'SuperAdmin')) }]">
              <member-search-select
                element-id="horseOwner"
                context="owner"
                :value="selectedOwners"
                :initial-options="ownerInitialOptions"
                @input="v => { selectedOwners = v }"
              />
              <div class="invalid-tooltip" :style="isInvalid ? 'display: block': ''">Please include the Owner of the horse.</div>
            </div>
            <small>This must be an ACA member</small>
            <small class="text-danger" v-if="horseOwnerData.deleted_at && horseOwnerData.id === Number(horse.owners[0])">Deleted</small>
          </div>

          <div class="col-12 col-xl-6 form-group">
            <div class="row">

              <div class="col-12 col-md-6 form-group">
                <label for="horseDob">Date of Birth</label>
                <input type="date" :disabled="!horse.active || (!is_owner && !userIsSecretary)" :class="['form-control form-date pr-xl-3', errorStatus === 'dob' ? 'is-invalid' : '']" id="horseDob" v-model="horse.dob" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                <div class="invalid-tooltip">Please include the Date of Birth of the horse{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</div>
              </div>

              <div class="col-12 col-md-6 form-group pl-xl-0">
                <label>Sex</label>
                <select v-model="horse.gender" required name="horseSex" class="form-control" :disabled="!horse.active || (!is_owner && !userIsSecretary)">
                  <option default></option>
                  <option value="Mare">Mare</option>
                  <option value="Gelding">Gelding</option>
                  <option value="Stallion">Stallion</option>
                </select>
                <div class="invalid-tooltip">Please include the Sex of the horse.</div>
              </div>

            </div>
          </div>

          <div class="col-12 col-md-6 mb-3 form-group">
            <label for="status">Horse Status</label>
            <select class="form-control" id="status" :required="(user === 'Admin' || user === 'SuperAdmin')" :disabled="!horse.active || userIsNotAdmin" v-model="horse.horse_status_id">
              <option v-for="(status, index) in $store.state.horseStatus" :value="status.id" :key="index">{{status.name}}</option>
            </select>
            <div class="invalid-tooltip">Please include the Status.</div>
            <small v-if="userIsNotAdmin">If you need to update your horses status please contact the ACA office in business hours</small>
          </div>

          <div class="col-12 col-md-6 mb-3 form-group" v-if="horse.boy_status_id">
            <label for="boyStatus">Beginning of Year Horse Status</label>
            <select class="form-control" id="boyStatus" required :disabled="!horse.active || userIsNotAdmin" v-model="horse.boy_status_id">
              <option disabled :selected="!horse.boy_status_id"></option>
              <option v-for="(status, statusIndex) in $store.state.horseStatus" :key="statusIndex" :value="status.id" :selected="horse.boy_status_id === status.id">{{status.name}}</option>
            </select>
            <div class="invalid-tooltip">Please include the Rider Status.</div>
            <small v-if="userIsNotAdmin">If you need to update your horses status please contact the ACA office in business hours</small>
          </div>

          <div class="col-12 col-md-6 form-group">
            <label for="horseSire">Sire <small>(optional)</small></label>
            <div :class="['not-required', { 'click-events-none' : (!horse.active || (!userIsSecretary)) }]">
              <horse-search-select
                element-id="horseSire"
                v-model="selectedSire"
                :disabled="!horse.active || !userIsSecretary"
              />
            </div>
          </div>

          <div class="col-12 col-md-6 form-group">
            <label for="horseDam">Dam <small>(optional)</small></label>
            <div :class="['not-required', { 'click-events-none' : (!horse.active || (!is_owner && !userIsSecretary)) }]">
              <horse-search-select
                element-id="horseDam"
                v-model="selectedDam"
                :disabled="!horse.active || (!is_owner && !userIsSecretary)"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label for="ashs"><abbr title="Australian Stock Horse Number">ASHS#</abbr> <small>(optional)</small></label>
            <input type="text"
              :class="['form-control', errorStatus === 'ashs' ? 'is-invalid' : '']"
              id="ashs"
              v-model="horse.ashs_number"
              maxlength="15"
              :disabled="!horse.active || (!is_owner && !userIsSecretary)">
            <div class="invalid-tooltip">Please include a different ASHS#. This one is taken.</div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <!-- AQHA# is AQHS# -->
            <label for="aqhs"><abbr title="Australian Quarter Horse Number">AQHA#</abbr> <small>(optional)</small></label>
            <input type="text"
              :class="['form-control', errorStatus === 'aqhs' ? 'is-invalid' : '']"
              id="aqhs"
              v-model="horse.aqhs_number"
              maxlength="15"
              :disabled="!horse.active || (!is_owner && !userIsSecretary)">
            <div class="invalid-tooltip">Please include a different AQHA#. This one is taken.</div>
          </div>

          <div class="col-12 col-md-6 form-group">
            <label for="ownerDisplayName">Horse Owner Display Name <small>(optional)</small></label>
            <input type="text" class="form-control" id="ownerDisplayName" v-model="horse.owner_display_name" :disabled="!horse.active || (!is_owner && !userIsSecretary)">
            <small>This is how the horse owner will be displayed on the draws and the leaderboard</small>
          </div>

          <div class="col-12 col-md-6 form-group">
            <label for="proofOfAge">Proof of Age</label>
            <div class="custom-file">
              <input type="file"
                     :class="['custom-file-input font-weight-normal', { 'is-invalid': errorStatus === 'file' }]"
                     id="proofOfAge" accept="image/*, application/pdf" :disabled="(!is_owner && !userIsSecretary) || !horse.active" @change="fileUpload">
              <label :class="[{ 'no-after': (!is_owner && !userIsSecretary) || !horse.active}, 'custom-file-label font-weight-normal']"
                     for="proofOfAge">{{ file_name ? file_name : 'Choose file...' }}</label>
              <small>jpg, jpeg, png, tiff, gif, pdf file type required.</small>
              <button
                v-if="file_name"
                type="button"
                class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
                :disabled="(!is_owner && !userIsSecretary) || !horse.active"
                @click="deleteAttachment"
              >
                <span class="sr-only">Delete File</span>
                <div class="btn-remove">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                    <g fill="currentColor">
                      <path
                        d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/>
                      <path
                        d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/>
                      <path
                        d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/>
                    </g>
                  </svg>
                </div>
              </button>
            </div>
            <div class="attached-file-wrapper" v-if="this.file_path">
              {{ this.exists_file_name }}
              <div class="btn-remove-file" v-if="(is_owner || userIsSecretary) && horse.active" @click="deleteExistsAttachment">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                  <g fill="currentColor">
                    <path
                      d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path>
                  </g>
                </svg>
              </div>
              <div class="btn-download-file" @click="downloadAgeProofFile(horse.id)">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                  <g fill="currentColor">
                    <path
                      d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"></path>
                    <path
                      d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"></path>
                  </g>
                </svg>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6 pr-xl-4 form-group"  v-if="(user === 'Admin' || user === 'SuperAdmin')">
            <div class="row">
              <div class="col-12 col-md-6 form-group">
                <label for="horseAchPoints">Historic ACH points:</label>
                <input @input="actionPointAch" type="text" :class="['form-control']" id="horseAchPoints" v-model="horse.historic_point_ach"  min="0.00" max="1000.00">
              </div>
              <div class="col-12 col-md-6 form-group">
                <label for="horseRomPoints">Historic ROM points:</label>
                <input @input="actionPointRom" type="text" :class="['form-control']" id="horseRomPoints" v-model="horse.historic_point_rom"  min="0.00" max="1000.00">
              </div>
            </div>
            <div class="row">
              <div class="col-12 col-md-6 form-group">
                <label for="horseAchPoints">Total ACH points:</label>
                <input type="text" :class="['form-control']" id="horseAchPoints" v-model="totalAch" :disabled="1">
              </div>
              <div class="col-12 col-md-6 form-group">
                <label for="horseRomPoints">Total ROM points:</label>
                <input type="text" :class="['form-control']" id="horseRomPoints" v-model="totalRom" :disabled="1">
              </div>
            </div>
          </div>

          <div class="col-12 form-group" v-if="(user === 'Admin' || user === 'SuperAdmin')">
            <div class="row">
              <div class="col-12 col-md-6">
                <div class="custom-control custom-checkbox">
                  <input type="checkbox" class="custom-control-input" id="horseRegistered" v-model="horse.registered" @change="toggleRegisteredDate()" :disabled="!horse.active">
                  <label class="custom-control-label" for="horseRegistered">Registered</label>
                </div>
              </div>
              <div v-if="horse.registered" class="col-12 col-md-6">
                <label for="horseRegisteredDate">Registered Date</label>
                <input type="date" :class="['form-control form-date', errorStatus === 'registration_date' && errors ? 'is-invalid' : '']" id="horseRegisteredDate" required v-model="horse.registration_date" :disabled="!horse.active" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                <div class="invalid-tooltip">Please include the Registered Date of the horse{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</div>
              </div>
            </div>
          </div>

          <div class="col-12 form-group" v-if="user != 'Admin' && user != 'SuperAdmin' && isMember">
            <div class="row">
              <div class="col-12 col-md-6">
                <button type="button" class="btn btn-primary mb-3" @click="unlinkMemberHorse(horse.id)">Unlink</button>
              </div>
            </div>
          </div>

          <div class="col-12 d-flex d-lg-none justify-content-end">
            <div class="w-100">
              <router-link
                v-if="user === 'Admin' || user === 'SuperAdmin'"
                class="btn btn-outline-primary btn-block mb-2"
                :to="{ name: 'Merge Horse', params: { slug: horse.id } }"
              >Merge Horse</router-link>
              <button :class="['btn btn-block-mob', processing ? 'btn-primary-dark' : 'btn-primary']" @click="handleSubmit">
              {{ !processing ? 'Update' : '' }}
              <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
              </button>
            </div>
          </div>
        </div>

        <!-- use the modal component, pass in the prop -->
        <modal v-if="showModal" @close.prevent="showModal = false">
          <div slot="body">
            <div class="alert alert-warning">
              Changes to your registered horse must first be approved. Send to admin for approval?
            </div>
            <div class="d-flex justify-content-center">
              <button class="btn btn-outline-primary" @click.prevent="showModal = false">Cancel</button>
              <button class="btn btn-primary ml-2" @click.prevent="updateHorse();showModal = false">Confirm</button>
            </div>
          </div>
        </modal>
      </section>
    </div>

    <div v-else class="col px-0 pr-lg-3">
      <section class="bg-white rounded mb-4 p-3 p-sm-4">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
      </section>
    </div>

    <desktop-action-bar v-if="is_owner || userIsSecretary">
      <button :class="['btn btn-block', processing ? 'btn-primary-dark' : 'btn-primary']" @click="handleSubmit">
        {{ !processing ? 'Update' : '' }}
        <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
      </button>
      <router-link
        v-if="user === 'Admin' || user === 'SuperAdmin'"
        class="btn btn-outline-primary btn-block mt-2"
        :to="{ name: 'Merge Horse', params: { slug: horse.id } }"
      >Merge Horse</router-link>
    </desktop-action-bar>
  </form>
</template>

<script>
import axios from 'axios'
import HANDLESUBMIT from '@/mixins/formValidation.js'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import modal from '@/components/Modal'
import DesktopActionBar from '@/components/DesktopActionBar'
import HorseSearchSelect from '@/components/Horses/HorseSearchSelect'
import MemberSearchSelect from '@/components/Members/MemberSearchSelect'

export default {
  name: 'HorseProfileDetails',
  props: ['horseDetails'],
  mixins: [HANDLESUBMIT, LEGACYBROWSERS],
  components: {
    'horse-search-select': HorseSearchSelect,
    'member-search-select': MemberSearchSelect,
    modal,
    DesktopActionBar
  },
  data() {
    return {
      horse: { ...this.horseDetails },
      horseOwnerData: {},
      // Pre-resolved {id, label} for the currently-assigned owner/rider(s) — fed to
      // MemberSearchSelect as `initial-options` so an owner who no longer matches the picker's
      // own dataset (soft-deleted, or SecretaryOnly — see memberLabel()) still shows a chip
      // without a doomed /v2/members/options?ids= lookup (that context excludes both).
      ownerInitialOptions: [],
      memberInitialOptions: [],
      selectedOwners: [],
      selectedMembers: [],
      selectedSire: [],
      selectedDam: [],
      user: localStorage.role,
      showModal: false,
      processing: false,
      errors: '',
      errorStatus: '',
      is_owner: false,
      isMember: false,
      userIsSecretary: false,
      userIsNotAdmin: false,
      file_name: '',
      file: '',
      file_path: '',
      exists_file_name: '',
      totalAch: this.totalAch,
      totalRom: this.totalRom
    }
  },
  methods: {
    updateHorse() {
      if (!this.errors) {
        if (this.file && this.file.size > 1024 * 1024) {
          this.errorStatus = 'file'
          this.errors = `The Proof of Age file may not be greater than 1 mb.`
        }
        this.processing = true

        const horseData = {
          id: this.horse.id,
          name: this.horse.name,
          dob: this.horse.dob,
          gender: this.horse.gender,
          owners: this.horse.owners,
          user_id: this.selectedMembers || (this.horse.user ? this.horse.user.id : null),
          horse_status_id: this.horse.horse_status_id,
          sire_id: this.horse.sire,
          dam_id: this.horse.dam,
          ashs_number: this.horse.ashs_number,
          aqhs_number: this.horse.aqhs_number,
          active: this.horse.active,
          registration_date: this.horse.registration_date,
          notes: this.horse.notes,
          boy_status_id: this.horse.boy_status_id,
          file_path: this.file_path,
          historic_point_ach: this.horse.historic_point_ach,
          historic_point_rom: this.horse.historic_point_rom,
          owner_display_name: this.horse.owner_display_name
        }

        const formData = new FormData()
        formData.append('file', this.file)
        formData.append('fields', JSON.stringify(horseData))
        // eslint-disable-next-line
        axios.post(`${this.$api}${this.userIsSecretary && !this.is_owner ? '/admin/horse-update' : (this.is_owner || this.horse.active == false) ? '/horse/update' : '/'}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
          .then((response) => {
            this.processing = false
            // eslint-disable-next-line
            // console.log(response)
            if (response.data.status === 'success') {
              const isAdminDetails = this.$route.path.match('/admin-horses')
              const isSecretaryDetails = this.$route.path.match('/secretary-horses')
              this.$router.push({
                path: isAdminDetails ? '/admin-horses' : isSecretaryDetails ? '/secretary-horses' : '/horses',
                params: {
                  // eslint-disable-next-line
                  success: `${(this.userIsSecretary && !this.is_owner) || ((this.horseOwnerData.id.toString() === localStorage.userID) || this.horse.active == false)
                    ? 'Horse updated successfully'
                    : 'Changes to registered horse successfully sent for approval'}.`
                }
              })
            } else if (response.data.status === 'horse_limit') {
              this.errors = response.data.message
            } else if (response.data.status === 'validation_error') {
              if (response.data.errors.dob) {
                // DoB
                this.success = ''
                this.errorStatus = 'dob'
                this.errors = `Please include the Date of Birth in the format${this.isLegacy() ? ' yyyy-mm-dd' : ' dd/mm/yyyy'}.`
              } else if (response.data.errors.ashs_number) {
                // ashs_number
                this.success = ''
                this.errorStatus = 'ashs'
                this.errors = `The ASHS number has already been taken.`
              } else if (response.data.errors.aqhs_number) {
                // aqhs_number
                this.success = ''
                this.errorStatus = 'aqhs'
                this.errors = `The AQHA number has already been taken.`
              } else if (response.data.errors.file) {
                this.success = ''
                this.errorStatus = 'file'
                this.errors = Object.values(response.data.errors.file).flat()[0]
              }
            } else if (response.data.status === 'invalid_data') {
              this.success = ''
              this.errorStatus = ''
              this.errors = response.data.message
            } else if (response.data.status === 'verification_error') {
              this.errorStatus = 'verification_error'
              this.errors = response.data.errors.message
            } else {
              this.success = ''
              this.errorStatus = ''
              this.errors = `Something went wrong. Please reload the page.`
            }
          })
          .catch((error) => {
            if (error.response.status === 413) {
              this.success = ''
              this.errorStatus = 'file'
              this.errors = `The Proof of Age file may not be greater than 1 mb.`
            } else {
              this.success = ''
              this.errorStatus = ''
              this.errors = `Something went wrong. Please reload the page.`
            }
            this.processing = false
            // eslint-disable-next-line
            // console.log(error)
          })
      }
    },
    update(event) {
      event.preventDefault()

      setTimeout(() => {
        if (this.horse.owners.length > 0 && this.formComplete) {
          // eslint-disable-next-line
          if (this.userIsSecretary || this.is_owner) {
            this.updateHorse()
          } else {
            this.showModal = true
          }
        }
      }, 10)
    },
    toggleRegisteredDate() {
      if (this.horse.registered === false) {
        this.horse.registration_date = null
      }
    },
    unlinkMemberHorse(horse_id) {
      if (horse_id) {
        axios.delete(`${this.$api}/horse/unlink-member`, {
          params: { horse_id }
        }).then((response) => {
          if (response.data.status === 'success') {
            this.$router.push('/horses')
          }
        }
        ).catch()
      }
    },
    downloadAgeProofFile(horse_id) {
      if (horse_id) {
        axios.get(`${this.$api}/horse/download/${horse_id}`).then((response) => {
          window.open(response.data.path, '_blank')
        })
      }
    },
    fileUpload(event) {
      this.errorStatus = ''
      this.errors = ''
      this.file_name = ''
      const uploadedFile = event.target.files[0]
      this.file = uploadedFile
      if (uploadedFile && this.file.size > 1024 * 1024) {
        this.success = ''
        this.errorStatus = 'file'
        this.errors = `The Proof of Age file may not be greater than 1 mb.`
      }
      if (uploadedFile) {
        this.file_name = uploadedFile.name
      }
      this.deleteExistsAttachment()
    },
    deleteAttachment() {
      delete this.file
      this.file = undefined
      this.file_name = ''
      this.errorStatus = ''
      this.errors = ''
    },
    deleteExistsAttachment() {
      this.file_path = ''
      this.exists_file_name = ''
    },
    actionPointAch(event) {
      const value = event.target.value.replaceAll(/[^0-9.]/g, '')
      this.horse.historic_point_ach = value
      this.totalAch = (parseFloat(this.horse.ach_points) + parseFloat(value)).toFixed(2)
    },
    actionPointRom(event) {
      const value = event.target.value.replaceAll(/[^0-9.]/g, '')
      this.horse.historic_point_rom = value
      this.totalRom = (parseFloat(this.horse.rom_points) + parseFloat(value)).toFixed(2)
    },
    // Owner/rider pickers (MemberSearchSelect, context=owner) only offer live, non-SecretaryOnly
    // members — if a horse already has a soft-deleted or SecretaryOnly user attached, that id
    // would come back empty from a fresh lookup. Building the label from data the horse's own
    // fetch already carries (see mounted()) keeps the chip visible and flags it for cleanup,
    // without ever calling the picker API for an id it can't answer.
    buildMemberLabel(person) {
      let label = `${person.member_number}: ${person.first_name.replace(/[^a-z0-9áéíóúñü .,_-]/gim, "")} ${person.last_name.replace(/[^a-z0-9áéíóúñü .,_-]/gim, "")}`
      if (person.role === 'SecretaryOnly') {
        label += ' (Secretary - need to delete)'
      }
      return label
    }
  },
  computed: {
    isInvalid() {
      return this.wasValidated && this.horse.owners.length === 0
    },
    isUserInvalid() {
      return this.wasValidated && !this.horse.user_id
    }
  },
  created() {
    if (!this.horse.status) {
      this.horse.status = { name: null }
    }
  },
  mounted() {
    /* Check if a user role is Admin or Secretary or Secretary Only */
    if ((this.user === 'Admin' || this.user === 'SuperAdmin') || this.user === 'Secretary' || this.user === 'SecretaryOnly') {
      this.userIsSecretary = true
    }
    if ((this.user !== 'Admin' && this.user !== 'SuperAdmin')) {
      this.userIsNotAdmin = true
    }
    /* Get latest horse data */
    axios.post(`${this.$api}/horse`, { id: (this.$route.params && this.$route.params.slug && !isNaN(parseInt(this.$route.params.slug, 10))) ? this.$route.params.slug : this.horse.id }).then((response) => {
      if (response.data.status === 'success') {
        this.horse = { ...response.data.data }
        this.is_owner = response.data.is_owner
        this.$store.commit('updateViewedHorse', this.horse)
        this.totalAch = (parseFloat(this.horse.historic_point_ach) + parseFloat(this.horse.ach_points)).toFixed(2)
        this.totalRom = (parseFloat(this.horse.historic_point_rom) + parseFloat(this.horse.rom_points)).toFixed(2)
        this.selectedSire = this.horse.sire_id ? [this.horse.sire_id.toString()] : []
        this.selectedDam = this.horse.dam_id ? [this.horse.dam_id.toString()] : []
        this.horse.user_id = this.horse.user_id ? [this.horse.user_id.toString()] : []
        /* Assigning owner data to separate variable because weird stuff is happening */
        if (response.data.data.owners && response.data.data.owners.length > 0) {
          this.horseOwnerData = { ...response.data.data.owners[0] }
        }
        this.selectedMembers = response.data.data.members.map(a => a.id.toString())
        this.memberInitialOptions = response.data.data.members.map(m => ({ id: m.id, label: this.buildMemberLabel(m) }))
        this.isMember = this.selectedMembers.includes(localStorage.userID.toString())
        if (response.data.data.owners.length > 0 && this.horseOwnerData.id) {
          this.selectedOwners = [this.horseOwnerData.id.toString()]
          this.ownerInitialOptions = [{ id: this.horseOwnerData.id, label: this.buildMemberLabel(this.horseOwnerData) }]
        }
        if (response.data.data.age_proof_file_path) {
          this.file_path = response.data.data.age_proof_file_path
          this.exists_file_name = response.data.data.age_proof_file_name
        }
      } else {
        // Any non-success (undefined_error, invalid_user, …). This page gets no horse object
        // passed in, so `POST /horse` is the only data source — without this the spinners
        // would just hang. Bounce back to the list with a message.
        const horseError = response.data.status === 'invalid_user'
          ? "You don't have access to that horse."
          : "That horse couldn't be found."
        this.$router.push({ path: this.userIsNotAdmin ? '/horses' : '/admin-horses', params: { horseError } }).catch(() => {})
      }
    }).catch(() => {
      this.$router.push({ path: this.userIsNotAdmin ? '/horses' : '/admin-horses', params: { horseError: 'Unable to load that horse. Please try again.' } }).catch(() => {})
    })
  },
  watch: {
    selectedOwners() {
      this.horse.owners = this.selectedOwners
    },
    selectedMembers() {
      this.errors = ''
      this.errorStatus = ''
      this.success = ''
    },
    selectedSire() {
      this.horse.sire = this.selectedSire
    },
    selectedDam() {
      this.horse.dam = this.selectedDam
    },
    horse: {
      handler() {
        this.errors = ''
        this.errorStatus = ''
        this.success = ''
      },
      deep: true
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Horses/Profile/HorseProfileDetails.vue